import { useState, useEffect } from 'react'
import PublicLayout from '@/components/layout/PublicLayout'
import { detectRegion, type Region } from '@/utils/time'
import { REGION_DATA } from '@/components/layout/HealthDropdown'

const CRISIS_LINE: Record<string, string> = {
  AU: 'Lifeline on 13 11 14',
  UK: 'Samaritans on 116 123',
  US: '988 Suicide & Crisis Lifeline on 988',
  NZ: 'Lifeline Aotearoa on 0800 543 354',
}

export default function About({ embedded }: { embedded?: boolean }) {
  const [region, setRegion] = useState<Region>(detectRegion)

  useEffect(() => {
    const handler = (e: Event) => setRegion((e as CustomEvent).detail)
    window.addEventListener('ssdl:region-change', handler)
    return () => window.removeEventListener('ssdl:region-change', handler)
  }, [])

  const crisisLine = CRISIS_LINE[region] || CRISIS_LINE.AU
  const emergency = REGION_DATA[region]?.emergency || '000'

  const Wrapper = embedded ? ({ children }: { children: React.ReactNode }) => <>{children}</> : PublicLayout

  return (
    <Wrapper>
      <div className="flex-1 flex flex-col items-center p-4 py-8 md:py-12">
        <div className="w-full max-w-3xl space-y-6">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <svg className="w-12 h-12 text-[var(--accent-teal)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)]">About SSDL</h1>
            <p className="text-[var(--text-secondary)] mt-2">Why we built this, and who it's for</p>
          </div>

          {/* Our Mission */}
          <div className="card">
            <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-3">Our Mission</h2>
            <p className="text-[var(--text-secondary)] leading-relaxed">
              SSDL was built for one reason: to help people who want to understand their relationship with alcohol. Whether you're trying to cut back, staying mindful of your habits, or preparing for a conversation with your doctor, we believe tracking is a powerful first step toward change.
            </p>
            <p className="text-[var(--text-secondary)] leading-relaxed mt-3">
              We don't ask you to create an account. We don't ask for your email. We don't judge. We just give you a simple, private tool to see your numbers clearly — so you can make your own decisions.
            </p>
          </div>

          {/* Why SSDL Exists */}
          <div className="card">
            <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-3">Why SSDL Exists</h2>
            <p className="text-[var(--text-secondary)] leading-relaxed">
              Most drink tracking apps require you to sign up, share personal information, or connect social features — things that feel completely wrong when you're dealing with something as personal as drinking. Others are overly clinical, designed more for researchers than for the person sitting at their kitchen table wondering if they're drinking too much.
            </p>
            <p className="text-[var(--text-secondary)] leading-relaxed mt-3">
              SSDL strips it back to the essentials. Log your drinks, see your blood alcohol level in real-time, track your patterns over time, and download a report you can take to your GP. That's it. No social features, no gamification, no leaderboards — just you and your data.
            </p>
          </div>

          {/* How It Works */}
          <div className="card">
            <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-3">How It Works</h2>
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="shrink-0 w-8 h-8 rounded-full bg-[var(--accent-teal)] text-[var(--bg-primary)] flex items-center justify-center font-bold text-sm">1</div>
                <div>
                  <p className="text-[var(--text-primary)] font-medium">Get a passcode</p>
                  <p className="text-sm text-[var(--text-secondary)]">Hit "Get Started" and you'll receive a unique passcode. No email, no sign-up. Bookmark it or write it down — it's your key to your data.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="shrink-0 w-8 h-8 rounded-full bg-[var(--accent-teal)] text-[var(--bg-primary)] flex items-center justify-center font-bold text-sm">2</div>
                <div>
                  <p className="text-[var(--text-primary)] font-medium">Log your drinks</p>
                  <p className="text-sm text-[var(--text-secondary)]">Choose from 65+ pre-loaded drinks or add your own. Your BAC is calculated in real-time using the Widmark formula.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="shrink-0 w-8 h-8 rounded-full bg-[var(--accent-teal)] text-[var(--bg-primary)] flex items-center justify-center font-bold text-sm">3</div>
                <div>
                  <p className="text-[var(--text-primary)] font-medium">See your patterns</p>
                  <p className="text-sm text-[var(--text-secondary)]">Track sessions, sobriety streaks, worst cases, and trends over time. The app gives you the full picture — not just tonight.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="shrink-0 w-8 h-8 rounded-full bg-[var(--accent-teal)] text-[var(--bg-primary)] flex items-center justify-center font-bold text-sm">4</div>
                <div>
                  <p className="text-[var(--text-primary)] font-medium">Talk to your doctor</p>
                  <p className="text-sm text-[var(--text-secondary)]">Download a GP-ready PDF report summarising your consumption, risk level, and patterns. Walking into an appointment with data makes the conversation easier.</p>
                </div>
              </div>
            </div>
          </div>

          {/* What We Believe */}
          <div className="card">
            <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-3">What We Believe</h2>
            <p className="text-[var(--text-secondary)] leading-relaxed">
              We believe in awareness over restriction. Knowing your numbers is not about shame — it's about clarity. Most people who drink too much already know something isn't right. What they need isn't another lecture. They need a simple, private way to see the data and decide for themselves what to do about it.
            </p>
            <p className="text-[var(--text-secondary)] leading-relaxed mt-3">
              We also believe that talking to a GP is one of the most powerful things you can do. That's why the app includes a downloadable report designed specifically for that conversation. Your doctor doesn't judge you either — they just want to help.
            </p>
          </div>

          {/* Not Medical Advice */}
          <div className="card border-[var(--border)]">
            <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-3">Important Disclaimer</h2>
            <p className="text-[var(--text-secondary)] leading-relaxed">
              SSDL is a self-awareness tool, not a medical device. BAC calculations are estimates based on the Widmark formula and should not be used to determine fitness to drive, operate machinery, or make medical decisions. If you're concerned about your drinking, please talk to a healthcare professional. If you're in crisis, call {crisisLine} or your local emergency number ({emergency}).
            </p>
          </div>
        </div>
      </div>
    </Wrapper>
  )
}
